import { useEffect, useRef, useState } from 'react';

export interface ChatMsg {
  id: number | string;
  from: 'parent' | 'child';
  /** Which parent sent it (#674); absent on the child's messages and older parent ones. */
  parentId?: string;
  name: string;
  text: string;
  ts: number;
}

/**
 * A chat thread read like a group chat: my messages sit on the right, everyone else's on the left with their name. On
 * the parent side, `me` says which parent is looking, so a co-parent's messages show on the left under their name
 * (#674); older messages without a sender id are matched by name.
 */
export function ChatThread({
  messages,
  onSend,
  meSide,
  me,
  placeholder,
}: {
  messages: ChatMsg[];
  /** Resolving to false (it didn't send) puts the words back in the box. */
  onSend: (text: string) => void | boolean | Promise<boolean>;
  meSide: 'parent' | 'child';
  me?: { id: string; name: string };
  placeholder?: string;
}) {
  const mine = (m: ChatMsg) => m.from === meSide && (meSide !== 'parent' || !me || (m.parentId ? m.parentId === me.id : m.name === me.name));
  const [text, setText] = useState('');
  const logRef = useRef<HTMLDivElement>(null);
  // Show the newest message by scrolling the list itself; scrollIntoView would drag the whole page along too (#853).
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [messages]);

  return (
    <div className="chat">
      <div ref={logRef} className="chat-log" role="log" aria-live="polite">
        {messages.length === 0 && <p className="muted small chat-empty">No messages yet. Say hello 👋</p>}
        {messages.map((m) => (
          <div key={m.id} className={`chat-msg ${mine(m) ? 'me' : m.from === meSide ? 'them co-parent' : 'them'}`}>
            <span className="chat-name">{mine(m) ? (meSide === 'parent' ? 'You' : '') : m.name}</span>
            <span className="chat-bubble">{m.text}</span>
          </div>
        ))}
      </div>
      <form
        className="chat-input"
        onSubmit={(e) => {
          e.preventDefault();
          const t = text.trim();
          if (!t) return;
          const sent = onSend(t);
          setText('');
          void Promise.resolve(sent).then((ok) => ok === false && setText((now) => now || t));
        }}
      >
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder={placeholder ?? 'Type a message…'} />
        <button className="btn primary" disabled={!text.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}
